"use client";

import { useAppContext } from "@/context/Context";
import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { signIn, useSession } from "next-auth/react";
import Loading from "@/components/Common/Loading";
import Image from "next/image";
import Link from "next/link";
import logo from "../../public/images/logo/logo.webp";
import google from "../../public/images/sign-up/google.png";
import logoDark from "../../public/images/light/logo/logo-dark.webp";

/**
 * Componente para iniciar sesión.
 * Permite al usuario autenticarse con sus credenciales y redirige según corresponda.
 *
 * @returns {JSX.Element} - Página de inicio de sesión.
 */
const Signin = () => {
  const router = useRouter();
  const { status } = useSession();
  const { isLightTheme } = useAppContext();

  // Mensaje del servidor para mostrar errores de autenticación.
  const [serverMessage, setServerMessage] = useState("");
  // Indica si se está enviando el formulario.
  const [isSubmitting, setIsSubmitting] = useState(false);
  // URL de redirección posterior a la autenticación.
  const [callbackUrl, setCallbackUrl] = useState("/crear-clase");

  // Obtener callbackUrl de los parámetros de la URL.
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    setCallbackUrl(searchParams.get("callbackUrl") || "/crear-clase");
  }, []);

  // Redirigir al usuario si ya está autenticado.
  useEffect(() => {
    if (status === "authenticated") router.push(callbackUrl);
  }, [status, router, callbackUrl]);

  /**
   * Maneja el envío del formulario de inicio de sesión.
   *
   * @param {Event} e - Evento del formulario.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    // Validar el formulario.
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }

    // Limpiar mensaje de error y activar estado de envío.
    setServerMessage("");
    setIsSubmitting(true);

    try {
      const result = await signIn("credentials", {
        redirect: false,
        email: form.email.value,
        password: form.password.value,
        callbackUrl,
      });

      // Si la autenticación falla, se muestra el error; de lo contrario se redirige.
      if (!result.ok) {
        setIsSubmitting(false);
        setServerMessage(result.error || "Error al iniciar sesión.");
      } else {
        router.push(result.url);
      }
    } catch (error) {
      setIsSubmitting(false);
      setServerMessage(
        "Ocurrió un error. Por favor, intenta nuevamente más tarde."
      );
    }
  };

  // Mostrar el componente Loading si la sesión está cargando, se está enviando o el usuario ya está autenticado.
  if (status === "loading" || isSubmitting || status === "authenticated") {
    return <Loading />;
  }

  return (
    <main className="page-wrapper">
      <div className="signup-area">
        <div className="wrapper">
          <div className="row">
            <div className="col-lg-12 bg-color-blackest left-wrapper">
              <div className="sign-up-box">
                <div className="signup-box-top">
                  <Image
                    src={isLightTheme ? logo : logoDark}
                    width={193}
                    height={50}
                    alt="sign-in logo"
                  />
                </div>
                <div className="signup-box-bottom">
                  <div className="signup-box-content">
                    <h3 className="title mb--30">Iniciar sesión</h3>
                    {serverMessage && (
                      <div className="alert-server-message">
                        <p>{serverMessage}</p>
                      </div>
                    )}
                    <form onSubmit={handleSubmit} noValidate>
                      {[
                        {
                          name: "email",
                          type: "email",
                          placeholder: "Correo electrónico",
                          icon: "fa-envelope",
                          title: "Introduce un correo electrónico válido.",
                        },
                        {
                          name: "password",
                          type: "password",
                          placeholder: "Contraseña",
                          icon: "fa-lock",
                          minLength: 6,
                          title: "Contraseña requerida.",
                        },
                      ].map(({ name, ...props }) => (
                        <div className="input-section" key={name}>
                          <div className="icon">
                            <i
                              className={`fa-sharp fa-regular ${props.icon}`}
                            ></i>
                          </div>
                          <input
                            name={name}
                            required
                            maxLength={100}
                            {...props}
                          />
                        </div>
                      ))}
                      <div className="forget-text">
                        <Link
                          className="btn-read-more"
                          href="/recuperar-contraseña"
                        >
                          <span>Olvidé contraseña</span>
                        </Link>
                      </div>
                      <button
                        type="submit"
                        className="btn-default"
                        disabled={isSubmitting}
                      >
                        Ingresar
                        {isSubmitting && (
                          <i className="feather-loader spinner-icon"></i>
                        )}
                      </button>
                    </form>

                    <div className="signup-box-footer">
                      <div className="bottom-text">
                        ¿No tienes una cuenta?
                        <Link
                          className="btn-read-more ml--5"
                          href="/registrarse"
                        >
                          <span>Regístrate</span>
                        </Link>
                      </div>
                    </div>

                    <div className="text-social-area">
                      <hr />
                      <span>o</span>
                      <hr />
                    </div>
                    <div className="social-btn-grp">
                      <button
                        type="button"
                        className="btn-default btn-border"
                        onClick={() =>
                          signIn("google", { callbackUrl: "/acceso-google" })
                        }
                      >
                        <span className="icon-left">
                          <Image
                            src={google}
                            width={18}
                            height={18}
                            alt="Google Icon"
                          />
                        </span>
                        Iniciar sesión con Google
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Link className="close-button" href="/">
          <i className="fa-sharp fa-regular fa-x"></i>
        </Link>
      </div>
    </main>
  );
};

export default Signin;
